import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Scatter, Doughnut } from 'react-chartjs-2';
import { adminAPI } from '../../../services/api';
import ManagedDisclaimer from '../../../components/ManagedDisclaimer';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, ArcElement, Tooltip, Legend);

// Series palette — Drummond house colours (navy/gold/blue/green…), matches the
// example CMA comparison charts. Cycles when there are more series than colours.
const SERIES_COLOURS = [
  '#072040', '#f0a500', '#599BE3', '#91CF9B', '#a36b7e',
  '#6c757d', '#003366', '#ffc107', '#2d8a4e', '#c0504d',
];
const colourAt = (i) => SERIES_COLOURS[i % SERIES_COLOURS.length];

// Asset-class-group colours, aligned with the fact sheet generator
// (app/apps/factsheets/generator.py → ASSET_CLASS_CONFIG) so the strategy
// allocation donuts match the published fact sheets.
const FACTSHEET_AC_COLOURS = {
  'Global Alpha': '#BBCBD7',
  'Dynamic Plus Fund': '#BBCBD7',
  'Australian Equities': '#011E41',
  'International Equities': '#5E819C',
  'Real Estate': '#A6C6C1',
  'Infrastructure': '#337B91',
  'Cash': '#EFE6D5',
  'Government Bonds': '#F3BD48',
  'Corporate Debt': '#AA8090',
  'Commodities': '#4B3219',
  'Private Debt': '#874564',
  'Private Equity': '#6C757D',
  'Real Assets (Private Market)': '#127080',
  'EM Equities': '#7B68EE',
};

// Portfolios selected by default in the strategy table (Strategic 30–100 Plus).
const DEFAULT_SAA_CODES = ['DS30', 'DS50', 'DS70', 'DS90', 'DS100', 'DS100Plus'];

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

const InvestmentChartsTab = () => {
  // Raw data
  const [taxonomy, setTaxonomy] = useState(null);            // { broad_asset_classes, sub_asset_classes }
  const [scenarios, setScenarios] = useState([]);            // [{ id, name, returns_count, completeness... }]
  const [returnsByScenario, setReturnsByScenario] = useState({}); // { id: [{ sub_asset_class, annual_return, annual_volatility }] }
  const [saa, setSaa] = useState(null);                      // { portfolios, rows, financial_years }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Controls
  const [level, setLevel] = useState('sac');                 // 'sac' | 'group'
  const [excluded, setExcluded] = useState(() => new Set()); // axis keys to drop
  const [selectedScenarios, setSelectedScenarios] = useState([]);  // scenario ids
  const [selectedFY, setSelectedFY] = useState(null);
  const [selectedPorts, setSelectedPorts] = useState([]);    // blender_codes
  // SAA vintage comparison: one or more portfolios, two financial years.
  const [vintagePorts, setVintagePorts] = useState([]);      // blender_codes
  const [vintageA, setVintageA] = useState(null);
  const [vintageB, setVintageB] = useState(null);

  // Published strategy summary tables (FirstChoice + Strategic Series).
  const [strategy, setStrategy] = useState(null);            // strategy-summary API response
  const [thisYearScenario, setThisYearScenario] = useState(null); // CMA scenario for this-year return/vol
  const [lastYearScenario, setLastYearScenario] = useState(null); // scenario id for the last-year return row
  const [saaYear, setSaaYear] = useState(null);                   // baseline SAA financial year
  const [selectedSaas, setSelectedSaas] = useState([]);           // blender_codes shown as columns
  const [saaMenuOpen, setSaaMenuOpen] = useState(false);          // multi-select dropdown open state
  const [strategyErr, setStrategyErr] = useState(null);
  const [exporting, setExporting] = useState(false);              // image export in progress
  const exportRef = useRef(null);                                 // wraps the table + disclaimer for capture

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tax, scen, hist] = await Promise.all([
        adminAPI.getTaxonomy(),
        adminAPI.getCMAScenarios(),
        adminAPI.getHistoricSAA(),
      ]);
      const scenList = scen.scenarios || [];

      // Pull each scenario's returns in parallel.
      const returnsList = await Promise.all(
        scenList.map((s) => adminAPI.getScenarioReturns(s.id))
      );
      const rmap = {};
      scenList.forEach((s, i) => { rmap[s.id] = returnsList[i].returns || []; });

      setTaxonomy(tax);
      setScenarios(scenList);
      setReturnsByScenario(rmap);
      setSaa(hist);

      // Sensible defaults.
      const withReturns = scenList.filter((s) => (rmap[s.id] || []).length > 0);
      setSelectedScenarios(withReturns.map((s) => s.id));

      const fys = hist.financial_years || [];
      const fy0 = fys[0] ?? null;
      setSelectedFY(fy0);
      const portsInFy = (hist.portfolios || [])
        .map((p) => p.blender_code)
        .filter((bc) => (hist.rows || []).some((r) => r.blender_code === bc && r.financial_year === fy0));
      setSelectedPorts(portsInFy);

      // Vintage comparison defaults: newest vs previous FY, first portfolio with data.
      setVintageB(fys[0] ?? null);
      setVintageA(fys[1] ?? fys[0] ?? null);
      const firstHistPort = (hist.portfolios || [])
        .map((p) => p.blender_code)
        .find((bc) => (hist.rows || []).some((r) => r.blender_code === bc));
      setVintagePorts(firstHistPort ? [firstHistPort] : []);

      // Strategy summary tables — this-year = default scenario; last-year = most
      // recent non-default scenario with returns; baseline SAA = active vintage.
      const withData = scenList.filter((s) => (rmap[s.id] || []).length > 0);
      const defaultScen = withData.find((s) => s.is_default) || withData[0] || null;
      const lyDefault = withData.find((s) => !s.is_default) || defaultScen;
      const tyId = defaultScen ? defaultScen.id : null;
      const lyId = lyDefault ? lyDefault.id : null;
      const baseFy = hist.active_financial_year ?? (hist.financial_years || [])[0] ?? null;
      setThisYearScenario(tyId);
      setLastYearScenario(lyId);
      setSaaYear(baseFy);
      setStrategyErr(null);
      try {
        setStrategy(await adminAPI.getStrategySummary({
          thisYearScenarioId: tyId, lastYearScenarioId: lyId, financialYear: baseFy,
        }));
      } catch (e) {
        setStrategyErr(e.response?.data?.detail || 'Failed to load strategy summary');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Reload the strategy tables for the current control selections.
  const loadStrategy = useCallback(async (tyId, lyId, fy) => {
    setStrategyErr(null);
    try {
      setStrategy(await adminAPI.getStrategySummary({
        thisYearScenarioId: tyId, lastYearScenarioId: lyId, financialYear: fy,
      }));
    } catch (e) {
      setStrategyErr(e.response?.data?.detail || 'Failed to load strategy summary');
    }
  }, []);
  const onChangeThisYear = (id) => { setThisYearScenario(id); loadStrategy(id, lastYearScenario, saaYear); };
  const onChangeLastYear = (id) => { setLastYearScenario(id); loadStrategy(thisYearScenario, id, saaYear); };
  const onChangeSaaYear = (fy) => { setSaaYear(fy); loadStrategy(thisYearScenario, lastYearScenario, fy); };

  // Export the strategy table (donuts + rows + disclaimer) as a PNG. html2canvas
  // is loaded lazily so it only ships when someone actually exports.
  const exportImage = async () => {
    if (!exportRef.current) return;
    setExporting(true);
    setStrategyErr(null);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(exportRef.current, {
        scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = 'strategy-summary.png';
      a.click();
    } catch (e) {
      setStrategyErr('Image export failed: ' + (e?.message || String(e)));
    } finally {
      setExporting(false);
    }
  };

  // Flat list of every portfolio across both series, for the single-SAA selector.
  const allPortfolios = useMemo(
    () => (strategy?.suites || []).flatMap((s) => (s.portfolios || []).map((p) => ({ ...p, suite: s.suite }))),
    [strategy]
  );
  // Keep the selection valid as data reloads (persist by blender_code). On first
  // load default to Strategic 30–100 Plus; every portfolio remains available in
  // the dropdown to add as a column.
  useEffect(() => {
    if (!allPortfolios.length) return;
    const codes = allPortfolios.map((p) => p.blender_code);
    const fallback = DEFAULT_SAA_CODES.filter((c) => codes.includes(c));
    setSelectedSaas((prev) => {
      const kept = prev.filter((c) => codes.includes(c));
      return kept.length ? kept : (fallback.length ? fallback : codes);
    });
  }, [allPortfolios]);

  // Ordered axis items for the current level: [{ key, label, members:[sac codes] }].
  const axisItems = useMemo(() => {
    const subs = (taxonomy?.sub_asset_classes || [])
      .slice()
      .sort((a, b) => a.display_order - b.display_order);

    if (level === 'sac') {
      return subs.map((s) => ({
        key: s.sub_asset_class,
        label: s.sac_name || s.sub_asset_class,
        members: [s.sub_asset_class],
        group: s.broad_asset_class || '—',
      }));
    }

    // Group level: bucket SACs by broad_asset_class, ordered by the broad-class display_order.
    const broadOrder = {};
    (taxonomy?.broad_asset_classes || []).forEach((b) => { broadOrder[b.name] = b.display_order; });
    const buckets = {};
    subs.forEach((s) => {
      const g = s.broad_asset_class || '—';
      if (!buckets[g]) buckets[g] = [];
      buckets[g].push(s.sub_asset_class);
    });
    return Object.keys(buckets)
      .sort((a, b) => (broadOrder[a] ?? 999) - (broadOrder[b] ?? 999))
      .map((g) => ({ key: g, label: g, members: buckets[g], group: g }));
  }, [taxonomy, level]);

  // Stable colour per broad asset class, used to colour bars/points by asset type
  // when a single scenario is shown.
  const groupColour = useMemo(() => {
    const order = (taxonomy?.broad_asset_classes || [])
      .slice()
      .sort((a, b) => a.display_order - b.display_order)
      .map((b) => b.name);
    const m = {};
    order.forEach((name, i) => { m[name] = colourAt(i); });
    return (g) => m[g] || (order.length ? colourAt(order.length) : SERIES_COLOURS[0]);
  }, [taxonomy]);

  // Reset the exclude set whenever the level changes (keys differ between levels).
  useEffect(() => { setExcluded(new Set()); }, [level]);

  const visibleItems = useMemo(
    () => axisItems.filter((it) => !excluded.has(it.key)),
    [axisItems, excluded]
  );

  const toggleExclude = (key) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleScenario = (id) => {
    setSelectedScenarios((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const togglePort = (bc) => {
    setSelectedPorts((prev) =>
      prev.includes(bc) ? prev.filter((x) => x !== bc) : [...prev, bc]);
  };

  const toggleVintagePort = (bc) => {
    setVintagePorts((prev) =>
      prev.includes(bc) ? prev.filter((x) => x !== bc) : [...prev, bc]);
  };

  // When the FY changes, default the portfolio selection to those present in that FY.
  const onChangeFY = (fy) => {
    setSelectedFY(fy);
    const portsInFy = (saa?.portfolios || [])
      .map((p) => p.blender_code)
      .filter((bc) => (saa?.rows || []).some((r) => r.blender_code === bc && r.financial_year === fy));
    setSelectedPorts(portsInFy);
  };

  // Aggregate one scenario's per-SAC field to the current axis (average for group level).
  // Returns/vols are stored as decimals (e.g. 0.0544) — scale to percent for the charts.
  // (Historic SAA weights, handled separately below, are already stored as percent.)
  const aggregateReturns = useCallback((rows, field) => {
    const bySac = {};
    (rows || []).forEach((r) => { bySac[r.sub_asset_class] = num(r[field]) * 100; });
    return visibleItems.map((it) => {
      const vals = it.members.filter((m) => bySac[m] != null).map((m) => bySac[m]);
      return vals.length ? mean(vals) : null;
    });
  }, [visibleItems]);

  // Aggregate one portfolio's SAA weights (sum for group level) for the selected FY.
  const aggregateWeights = useCallback((blenderCode) => {
    const bySac = {};
    (saa?.rows || [])
      .filter((r) => r.blender_code === blenderCode && r.financial_year === selectedFY)
      .forEach((r) => { bySac[r.sub_asset_class] = num(r.weight); });
    return visibleItems.map((it) => {
      const present = it.members.filter((m) => bySac[m] != null);
      if (!present.length) return null;
      return present.reduce((sum, m) => sum + bySac[m], 0);
    });
  }, [saa, selectedFY, visibleItems]);

  // Aggregate one portfolio's SAA weights for an ARBITRARY financial year (used by
  // the vintage comparison). Mirrors aggregateWeights but parameterised by year.
  const weightsForFY = useCallback((blenderCode, fy) => {
    const bySac = {};
    (saa?.rows || [])
      .filter((r) => r.blender_code === blenderCode && r.financial_year === fy)
      .forEach((r) => { bySac[r.sub_asset_class] = num(r.weight); });
    return visibleItems.map((it) => {
      const present = it.members.filter((m) => bySac[m] != null);
      if (!present.length) return null;
      return present.reduce((sum, m) => sum + bySac[m], 0);
    });
  }, [saa, visibleItems]);

  // ---- Chart datasets -------------------------------------------------------
  const labels = visibleItems.map((it) => it.label);

  const chosenScenarios = useMemo(
    () => scenarios.filter((s) => selectedScenarios.includes(s.id)),
    [scenarios, selectedScenarios]
  );

  // With a single scenario, colour each bar by its asset-type group; otherwise
  // colour each scenario (series) consistently.
  const singleScenario = chosenScenarios.length === 1;
  const barColours = useMemo(
    () => visibleItems.map((it) => groupColour(it.group)),
    [visibleItems, groupColour]
  );

  const returnsData = useMemo(() => ({
    labels,
    datasets: chosenScenarios.map((s, i) => ({
      label: s.name,
      data: aggregateReturns(returnsByScenario[s.id], 'annual_return'),
      backgroundColor: singleScenario ? barColours : colourAt(i),
      barPercentage: 0.9,
      categoryPercentage: 0.8,
    })),
  }), [labels, chosenScenarios, returnsByScenario, aggregateReturns, singleScenario, barColours]);

  const volData = useMemo(() => ({
    labels,
    datasets: chosenScenarios.map((s, i) => ({
      label: s.name,
      data: aggregateReturns(returnsByScenario[s.id], 'annual_volatility'),
      backgroundColor: singleScenario ? barColours : colourAt(i),
      barPercentage: 0.9,
      categoryPercentage: 0.8,
    })),
  }), [labels, chosenScenarios, returnsByScenario, aggregateReturns, singleScenario, barColours]);

  const portName = useMemo(() => {
    const m = {};
    (saa?.portfolios || []).forEach((p) => { m[p.blender_code] = p.name; });
    return m;
  }, [saa]);

  const chosenPorts = useMemo(
    () => (saa?.portfolios || []).map((p) => p.blender_code).filter((bc) => selectedPorts.includes(bc)),
    [saa, selectedPorts]
  );

  // Portfolios chosen for the vintage comparison (in taxonomy order).
  const chosenVintagePorts = useMemo(
    () => (saa?.portfolios || []).map((p) => p.blender_code).filter((bc) => vintagePorts.includes(bc)),
    [saa, vintagePorts]
  );
  const singleVintagePort = chosenVintagePorts.length === 1 ? chosenVintagePorts[0] : null;

  const weightsData = useMemo(() => ({
    labels,
    datasets: chosenPorts.map((bc, i) => ({
      label: portName[bc] || bc,
      data: aggregateWeights(bc),
      backgroundColor: colourAt(i),
      barPercentage: 0.9,
      categoryPercentage: 0.8,
    })),
  }), [labels, chosenPorts, aggregateWeights, portName]);

  // Vintage levels: a SINGLE portfolio across two FYs, grouped bars per axis item.
  const vintageCompareData = useMemo(() => ({
    labels,
    datasets: (singleVintagePort && vintageA != null && vintageB != null) ? [
      { label: `FY${vintageA}`, data: weightsForFY(singleVintagePort, vintageA),
        backgroundColor: colourAt(0), barPercentage: 0.9, categoryPercentage: 0.8 },
      { label: `FY${vintageB}`, data: weightsForFY(singleVintagePort, vintageB),
        backgroundColor: colourAt(1), barPercentage: 0.9, categoryPercentage: 0.8 },
    ] : [],
  }), [labels, singleVintagePort, vintageA, vintageB, weightsForFY]);

  // Change between the two vintages (FYb − FYa), in percentage points — one series
  // per selected portfolio, so multiple portfolios' moves can be compared at once.
  const vintageDeltaData = useMemo(() => ({
    labels,
    datasets: (vintageA != null && vintageB != null) ? chosenVintagePorts.map((bc, i) => {
      const a = weightsForFY(bc, vintageA);
      const b = weightsForFY(bc, vintageB);
      const deltas = labels.map((_, idx) => {
        if (a[idx] == null && b[idx] == null) return null;
        return (b[idx] || 0) - (a[idx] || 0);
      });
      return {
        label: portName[bc] || bc,
        data: deltas,
        backgroundColor: colourAt(i),
        barPercentage: 0.9,
        categoryPercentage: 0.8,
      };
    }) : [],
  }), [labels, chosenVintagePorts, vintageA, vintageB, weightsForFY, portName]);

  const scatterData = useMemo(() => ({
    datasets: chosenScenarios.map((s, i) => {
      const ret = aggregateReturns(returnsByScenario[s.id], 'annual_return');
      const vol = aggregateReturns(returnsByScenario[s.id], 'annual_volatility');
      const points = visibleItems
        .map((it, idx) => ({ x: vol[idx], y: ret[idx], cls: it.label, colour: groupColour(it.group) }))
        .filter((p) => p.x != null && p.y != null);
      return {
        label: s.name,
        data: points,
        // Single scenario → colour each point by its asset-type group.
        backgroundColor: singleScenario ? points.map((p) => p.colour) : colourAt(i),
        pointRadius: 5,
        pointHoverRadius: 7,
      };
    }),
  }), [chosenScenarios, returnsByScenario, aggregateReturns, visibleItems, singleScenario, groupColour]);

  // ---- Chart options --------------------------------------------------------
  const pctBarOptions = (title, showLegend = true) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: showLegend, position: 'bottom', labels: { font: { size: 11 }, boxWidth: 14 } },
      title: { display: true, text: title, color: 'var(--primary-color)', font: { size: 14, weight: 'bold' } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${num(ctx.parsed.y).toFixed(2)}%` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 60, minRotation: 0 } },
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.08)' }, ticks: { font: { size: 10 }, callback: (v) => `${v}%` } },
    },
  });

  // Delta chart: signed bars in percentage points (allows negatives).
  const deltaBarOptions = (title, showLegend = true) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: showLegend, position: 'bottom', labels: { font: { size: 11 }, boxWidth: 14 } },
      title: { display: true, text: title, color: 'var(--primary-color)', font: { size: 14, weight: 'bold' } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${num(ctx.parsed.y) >= 0 ? '+' : ''}${num(ctx.parsed.y).toFixed(2)} pp` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 60, minRotation: 0 } },
      y: { grid: { color: 'rgba(0,0,0,0.08)' }, ticks: { font: { size: 10 }, callback: (v) => `${v} pp` } },
    },
  });

  const scatterOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 14 } },
      title: { display: true, text: 'Risk vs Return', color: 'var(--primary-color)', font: { size: 14, weight: 'bold' } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const p = ctx.raw;
            return `${ctx.dataset.label} — ${p.cls}: ${num(p.y).toFixed(2)}% ret, ${num(p.x).toFixed(2)}% vol`;
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: 'Volatility (%)', font: { size: 11 } }, beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.08)' }, ticks: { font: { size: 10 }, callback: (v) => `${v}%` } },
      y: { title: { display: true, text: 'Expected Return (%)', font: { size: 11 } }, beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.08)' }, ticks: { font: { size: 10 }, callback: (v) => `${v}%` } },
    },
  };

  if (loading) return <div className="p-4 text-muted">Loading charts…</div>;
  if (error) {
    return (
      <div className="alert alert-danger d-flex justify-content-between align-items-center">
        <span>{error}</span>
        <button className="btn btn-sm btn-outline-danger" onClick={fetchAll}>Retry</button>
      </div>
    );
  }

  const fys = saa?.financial_years || [];
  const portsForFY = (saa?.portfolios || [])
    .filter((p) => (saa?.rows || []).some((r) => r.blender_code === p.blender_code && r.financial_year === selectedFY));
  // Portfolios that have any historic SAA rows (for the vintage comparison picker).
  const histPorts = (saa?.portfolios || [])
    .filter((p) => (saa?.rows || []).some((r) => r.blender_code === p.blender_code));

  const cardStyle = { border: '1px solid var(--light-color)', borderRadius: 8, padding: '1rem', background: '#fff', marginBottom: '1.25rem' };
  const chip = (active) => ({
    cursor: 'pointer', userSelect: 'none', fontSize: '0.78rem', padding: '2px 10px', borderRadius: 999,
    border: `1px solid ${active ? 'var(--primary-color)' : '#ccc'}`,
    background: active ? 'var(--primary-color)' : '#fff', color: active ? '#fff' : 'var(--gray-color)',
  });
  const selectClear = (onAll, onNone) => (
    <span className="small">
      <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '0.72rem' }} onClick={onAll}>Select all</button>
      <span className="text-muted mx-2">·</span>
      <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '0.72rem' }} onClick={onNone}>Clear all</button>
    </span>
  );
  const dataScenarioIds = scenarios.filter((s) => (returnsByScenario[s.id] || []).length > 0).map((s) => s.id);

  // ---- Strategy summary tables ---------------------------------------------
  const broadClasses = strategy?.broad_asset_classes || [];
  const lastYearName = strategy?.last_year_scenario?.name || lastYearScenario;
  const lastYearYear = (String(lastYearName || '').match(/\b(?:19|20)\d{2}\b/) || [])[0];
  const lastYearLabel = `${lastYearYear || lastYearName || 'Last Year'} Expected Return`;
  const thisYearName = strategy?.default_scenario?.name;

  const fmtPct1 = (d) => `${(num(d) * 100).toFixed(1)}%`;       // decimal → 1dp %
  const fmtIntPct = (v) => `${Math.round(num(v))}%`;            // already a whole %
  const fmtFracPct = (f) => `${Math.round(num(f) * 100)}%`;     // fraction → whole %
  const cleanName = (n) => (n || '').replace('First Choice', 'FirstChoice').replace(/ Portfolio$/, '');

  const donutFor = (p) => {
    const items = broadClasses
      .map((ac) => ({ ac, v: num(p.allocation?.[ac]) * 100 }))
      .filter((x) => x.v > 0.0001);
    return {
      labels: items.map((x) => x.ac),
      datasets: [{
        data: items.map((x) => x.v),
        backgroundColor: items.map((x) => FACTSHEET_AC_COLOURS[x.ac] || groupColour(x.ac)),
        borderColor: '#fff',
        borderWidth: 1,
      }],
    };
  };
  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '58%',
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${num(ctx.parsed).toFixed(1)}%` } },
    },
  };

  // Table cell styles (Drummond house: navy header bar, light section rows).
  const thCol = { background: 'var(--primary-color)', color: '#fff', fontSize: '0.72rem', fontWeight: 600, padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle' };
  const thFirst = { ...thCol, textAlign: 'left', minWidth: 220 };
  const sectionRow = { background: 'var(--light-color)', color: 'var(--primary-color)', fontWeight: 700, fontSize: '0.72rem', padding: '4px 8px', letterSpacing: '0.02em' };
  const labelCell = { fontSize: '0.74rem', padding: '3px 8px', color: 'var(--dark-color)', whiteSpace: 'nowrap' };
  const labelStrong = { ...labelCell, fontWeight: 600 };
  const valCell = { fontSize: '0.74rem', padding: '3px 8px', textAlign: 'center', color: 'var(--dark-color)' };
  const rowBorder = { borderTop: '1px solid var(--light-color)' };

  const selectedPortfolios = allPortfolios.filter((p) => selectedSaas.includes(p.blender_code));

  const toggleSaa = (bc) => setSelectedSaas((prev) =>
    prev.includes(bc) ? prev.filter((x) => x !== bc) : [...prev, bc]);
  const setSeries = (suiteObj, on) => {
    const codes = (suiteObj.portfolios || []).map((p) => p.blender_code);
    setSelectedSaas((prev) => on
      ? Array.from(new Set([...prev, ...codes]))
      : prev.filter((c) => !codes.includes(c)));
  };

  const metricRow = (ports, label, fmt, opts = {}) => (
    <tr style={opts.highlight ? { background: 'rgba(89,155,227,0.10)' } : undefined}>
      <td style={opts.strong ? labelStrong : labelCell}>{label}</td>
      {ports.map((p) => <td key={p.blender_code} style={valCell}>{fmt(p)}</td>)}
    </tr>
  );

  const renderTable = (ports) => {
    // Only asset-class rows with exposure in the displayed set.
    const visibleBroad = broadClasses.filter((ac) => ports.some((p) => num(p.allocation?.[ac]) > 0));
    return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 160 + ports.length * 90 }}>
        <thead>
          <tr>
            <th style={thFirst}>Asset Allocation Profiles</th>
            {ports.map((p) => <th key={p.blender_code} style={thCol}>{cleanName(p.display_name)}</th>)}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={labelCell} />
            {ports.map((p) => (
              <td key={p.blender_code} style={{ ...valCell, padding: 6 }}>
                <div style={{ height: 96, width: 96, margin: '0 auto' }}>
                  <Doughnut data={donutFor(p)} options={donutOptions} />
                </div>
              </td>
            ))}
          </tr>

          <tr><td style={sectionRow} colSpan={ports.length + 1}>Asset Allocation</td></tr>
          {visibleBroad.map((ac) => (
            <tr key={ac} style={rowBorder}>
              <td style={labelCell}>
                <span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 6,
                  verticalAlign: 'middle', background: FACTSHEET_AC_COLOURS[ac] || groupColour(ac),
                }} />
                {ac}
              </td>
              {ports.map((p) => <td key={p.blender_code} style={valCell}>{fmtFracPct(p.allocation?.[ac])}</td>)}
            </tr>
          ))}

          <tr><td style={{ ...sectionRow, paddingTop: 8 }} colSpan={ports.length + 1}>Growth Metrics</td></tr>
          {metricRow(ports, 'Growth Total', (p) => fmtIntPct(p.growth_total), { strong: true })}
          {metricRow(ports, 'Defense Total', (p) => fmtIntPct(p.defence_total), { strong: true })}
          {metricRow(ports, 'Growth Range', (p) => p.growth_range ?? '—')}
          {metricRow(ports, 'Defense Range', (p) => p.defence_range ?? '—')}
          {metricRow(ports, 'Neutral Growth Exposure', (p) => fmtIntPct(p.neutral_growth))}

          <tr><td style={{ ...sectionRow, paddingTop: 8 }} colSpan={ports.length + 1}>Risk Metrics</td></tr>
          {metricRow(ports, 'Time Horizon', (p) => p.time_horizon ?? '—')}
          {metricRow(ports, 'Expected Return**', (p) => fmtPct1(p.expected_return), { strong: true })}
          {metricRow(ports, 'Expected Volatility**', (p) => fmtPct1(p.expected_volatility), { strong: true })}
          {metricRow(ports, `${lastYearLabel}**`, (p) => fmtPct1(p.last_year_expected_return), { highlight: true })}
          {metricRow(
            ports,
            'Mgmt Fees & Costs',
            // Cost values arrive already as a percentage, so don't rescale.
            (p) => (p.expected_icr == null ? '—' : `${num(p.expected_icr).toFixed(2)}%`),
          )}
          {metricRow(ports, 'Estimated # of negative years over 20yr period', (p) => num(p.est_negative_years).toFixed(1))}
        </tbody>
      </table>
    </div>
    );
  };

  return (
    <div className="investment-charts-tab">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <strong style={{ color: 'var(--primary-color)' }}>Charts</strong>
        <span className="text-muted small">Compare expected returns, volatility and SAA weights across scenarios and portfolios.</span>
      </div>

      {/* ===== Strategy summary tables (FirstChoice + Strategic Series) ===== */}
      <div style={cardStyle}>
        <div className="d-flex align-items-start flex-wrap gap-3">
          <div>
            <strong style={{ color: 'var(--primary-color)' }}>Strategy Summary</strong>
            <div className="text-muted small" style={{ maxWidth: 540 }}>
              Asset allocation profiles and metrics for the published portfolio series. Pick the baseline
              SAA vintage and the this-year CMA scenario used to value it; the highlighted last-year row
              uses its own scenario against the same SAA.
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap gap-3 ms-auto">
            <div className="d-flex align-items-center gap-2">
              <label className="small mb-0 fw-semibold">Baseline SAA year</label>
              <select className="form-select form-select-sm" style={{ width: 110 }}
                value={saaYear ?? ''} onChange={(e) => onChangeSaaYear(parseInt(e.target.value, 10))}>
                {fys.length === 0 && <option value="">— none —</option>}
                {fys.map((fy) => <option key={fy} value={fy}>FY{fy}</option>)}
              </select>
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="small mb-0 fw-semibold">This-year scenario</label>
              <select className="form-select form-select-sm" style={{ width: 190 }}
                value={thisYearScenario ?? ''} onChange={(e) => onChangeThisYear(parseInt(e.target.value, 10))}>
                {scenarios.length === 0 && <option value="">— none —</option>}
                {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' ★' : ''}</option>)}
              </select>
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="small mb-0 fw-semibold">Last-year scenario</label>
              <select className="form-select form-select-sm" style={{ width: 190 }}
                value={lastYearScenario ?? ''} onChange={(e) => onChangeLastYear(parseInt(e.target.value, 10))}>
                {scenarios.length === 0 && <option value="">— none —</option>}
                {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' ★' : ''}</option>)}
              </select>
            </div>
          </div>
        </div>
        {strategyErr && <div className="text-danger small mt-2">{strategyErr}</div>}
        {!strategy && !strategyErr && <div className="text-muted small mt-2">Loading strategy summary…</div>}
      </div>

      {strategy && allPortfolios.length > 0 && (
        <div style={cardStyle}>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <label className="small mb-0 fw-semibold">Portfolios</label>
            <div style={{ position: 'relative' }}>
              <button type="button" className="btn btn-sm btn-outline-primary"
                onClick={() => setSaaMenuOpen((o) => !o)}>
                {selectedSaas.length} selected ▾
              </button>
              {saaMenuOpen && (
                <>
                  <div onClick={() => setSaaMenuOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1040 }} />
                  <div style={{
                    position: 'absolute', zIndex: 1041, marginTop: 4, minWidth: 250, maxHeight: 360,
                    overflowY: 'auto', background: '#fff', border: '1px solid var(--light-color)',
                    borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.15)', padding: '0.5rem 0.75rem',
                  }}>
                    {(strategy.suites || []).map((s) => (
                      <div key={s.suite} className="mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <strong className="small" style={{ color: 'var(--primary-color)' }}>{s.suite}</strong>
                          {selectClear(() => setSeries(s, true), () => setSeries(s, false))}
                        </div>
                        {(s.portfolios || []).map((p) => (
                          <label key={p.blender_code}
                            className="d-flex align-items-center gap-2 small"
                            style={{ cursor: 'pointer', padding: '2px 0' }}>
                            <input type="checkbox" checked={selectedSaas.includes(p.blender_code)}
                              onChange={() => toggleSaa(p.blender_code)} />
                            {cleanName(p.display_name)}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-auto"
              onClick={exportImage}
              disabled={exporting || !selectedPortfolios.length}
              title="Download the table and disclaimer as a PNG image"
            >
              <i className="bi bi-image me-1" />{exporting ? 'Exporting…' : 'Export as image'}
            </button>
          </div>
          {selectedPortfolios.length ? (
            <div ref={exportRef} style={{ background: '#fff', padding: 12 }}>
              {renderTable(selectedPortfolios)}
              <div style={{ fontSize: '0.62rem', color: 'var(--gray-color)', lineHeight: 1.4, marginTop: '0.75rem' }}>
                <div>
                  <strong>** </strong>Expected return and volatility are derived from Drummond&rsquo;s capital market
                  assumptions for the {thisYearName ? <strong>{thisYearName}</strong> : 'default'} scenario
                  applied to the FY{saaYear} SAA; the {lastYearLabel.toLowerCase()} row uses the
                  {' '}{lastYearName ? <strong>{lastYearName}</strong> : 'selected last-year'} scenario against the
                  same SAA. They are estimates, not guarantees of future performance.
                </div>
                <div className="mt-1">
                  <strong>Disclaimer:</strong>{' '}<ManagedDisclaimer dkey="tool_investment_charts" as="span" />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-muted small">Select at least one portfolio.</div>
          )}
        </div>
      )}

      {/* Shared controls */}
      <div style={cardStyle}>
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <label className="small mb-0 fw-semibold">Level</label>
            <div className="btn-group btn-group-sm" role="group">
              <button className={`btn btn-${level === 'group' ? 'primary' : 'outline-primary'}`}
                onClick={() => setLevel('group')}>Asset Class Group</button>
              <button className={`btn btn-${level === 'sac' ? 'primary' : 'outline-primary'}`}
                onClick={() => setLevel('sac')}>Sub Asset Class</button>
            </div>
          </div>
        </div>

        <hr className="my-3" />

        <div className="mb-2">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <label className="small fw-semibold mb-0">Exclude asset classes</label>
            {selectClear(() => setExcluded(new Set()), () => setExcluded(new Set(axisItems.map((it) => it.key))))}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {axisItems.map((it) => {
              const active = !excluded.has(it.key);
              return (
                <span key={it.key} style={chip(active)} onClick={() => toggleExclude(it.key)}
                  title={active ? 'Click to exclude' : 'Click to include'}>{it.label}</span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scenario selector */}
      <div style={cardStyle}>
        <div className="d-flex align-items-center justify-content-between mb-1">
          <label className="small fw-semibold mb-0">Scenarios (returns, volatility &amp; risk/return)</label>
          {selectClear(() => setSelectedScenarios(dataScenarioIds), () => setSelectedScenarios([]))}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {scenarios.map((s) => {
            const hasData = (returnsByScenario[s.id] || []).length > 0;
            const active = selectedScenarios.includes(s.id);
            return (
              <span key={s.id} style={{ ...chip(active), opacity: hasData ? 1 : 0.45 }}
                onClick={() => hasData && toggleScenario(s.id)}
                title={hasData ? '' : 'No expected returns saved for this scenario'}>
                {s.name}{s.is_default ? ' ★' : ''}
              </span>
            );
          })}
        </div>
      </div>

      {/* Returns + Volatility charts */}
      <div style={cardStyle}>
        <div style={{ height: 360 }}>
          <Bar data={returnsData} options={pctBarOptions('Expected Returns', !singleScenario)} />
        </div>
        {singleScenario && (
          <div className="text-muted small text-center mt-1">Bars coloured by asset-class group</div>
        )}
      </div>
      <div style={cardStyle}>
        <div style={{ height: 360 }}>
          <Bar data={volData} options={pctBarOptions('Volatility', !singleScenario)} />
        </div>
        {singleScenario && (
          <div className="text-muted small text-center mt-1">Bars coloured by asset-class group</div>
        )}
      </div>

      {/* Risk-Return scatter */}
      <div style={cardStyle}>
        <div style={{ height: 380 }}>
          <Scatter data={scatterData} options={scatterOptions} />
        </div>
      </div>

      {/* SAA weights controls + chart */}
      <div style={cardStyle}>
        <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
          <div className="d-flex align-items-center gap-2">
            <label className="small mb-0 fw-semibold">Financial Year</label>
            <select className="form-select form-select-sm" style={{ width: 120 }}
              value={selectedFY ?? ''} onChange={(e) => onChangeFY(parseInt(e.target.value, 10))}>
              {fys.length === 0 && <option value="">— none —</option>}
              {fys.map((fy) => <option key={fy} value={fy}>FY{fy}</option>)}
            </select>
          </div>
          <div>
            <div className="d-flex align-items-center gap-3 mb-1">
              <label className="small fw-semibold mb-0">Portfolios</label>
              {portsForFY.length > 0 && selectClear(
                () => setSelectedPorts(portsForFY.map((p) => p.blender_code)),
                () => setSelectedPorts([]))}
            </div>
            <div className="d-flex flex-wrap gap-2">
              {portsForFY.length === 0 && <span className="text-muted small">No portfolios in this FY</span>}
              {portsForFY.map((p) => {
                const active = selectedPorts.includes(p.blender_code);
                return (
                  <span key={p.blender_code} style={chip(active)} onClick={() => togglePort(p.blender_code)}
                    title={portName[p.blender_code] || ''}>{p.blender_code}</span>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{ height: 360 }}>
          <Bar data={weightsData} options={pctBarOptions(`SAA Weights${selectedFY ? ` — FY${selectedFY}` : ''}`)} />
        </div>
      </div>

      {/* SAA vintage comparison — one portfolio across two financial years */}
      <div style={cardStyle}>
        <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
          <strong className="small" style={{ color: 'var(--primary-color)' }}>SAA Vintage Comparison</strong>
          <span className="text-muted small">How a portfolio&rsquo;s strategic weights changed between two financial years.</span>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
          <div className="d-flex align-items-center gap-2">
            <label className="small mb-0 fw-semibold">Compare</label>
            <select className="form-select form-select-sm" style={{ width: 110 }}
              value={vintageA ?? ''} onChange={(e) => setVintageA(parseInt(e.target.value, 10))}>
              {fys.length === 0 && <option value="">— none —</option>}
              {fys.map((fy) => <option key={fy} value={fy}>FY{fy}</option>)}
            </select>
            <span className="text-muted small">vs</span>
            <select className="form-select form-select-sm" style={{ width: 110 }}
              value={vintageB ?? ''} onChange={(e) => setVintageB(parseInt(e.target.value, 10))}>
              {fys.length === 0 && <option value="">— none —</option>}
              {fys.map((fy) => <option key={fy} value={fy}>FY{fy}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-2">
          <div className="d-flex align-items-center gap-3 mb-1">
            <label className="small fw-semibold mb-0">Portfolios</label>
            {histPorts.length > 0 && selectClear(
              () => setVintagePorts(histPorts.map((p) => p.blender_code)),
              () => setVintagePorts([]))}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {histPorts.length === 0 && <span className="text-muted small">No portfolios with historic SAA</span>}
            {histPorts.map((p) => {
              const active = vintagePorts.includes(p.blender_code);
              return (
                <span key={p.blender_code} style={chip(active)} onClick={() => toggleVintagePort(p.blender_code)}
                  title={p.name || ''}>{p.blender_code}</span>
              );
            })}
          </div>
        </div>
        <div style={{ height: 360 }}>
          <Bar data={vintageDeltaData}
            options={deltaBarOptions(`SAA Change FY${vintageA} → FY${vintageB} (percentage points)`)} />
        </div>
        {singleVintagePort ? (
          <div style={{ height: 360, marginTop: '1rem' }}>
            <Bar data={vintageCompareData}
              options={pctBarOptions(`SAA Weights — ${singleVintagePort} (FY${vintageA} vs FY${vintageB})`)} />
          </div>
        ) : (
          <div className="text-muted small text-center mt-2">
            Select a single portfolio to also see its year-on-year weight levels side by side.
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestmentChartsTab;
